/**
 * Google Identity Services helpers for PWA-safe Google authentication.
 *
 * Installed PWAs can lose Firebase redirect state when the provider flow opens
 * in a different browser context. GIS keeps the authentication response on the
 * login page and returns a Google ID token, which Firebase exchanges through
 * signInWithCredential. Firebase remains the only application auth runtime.
 */

import { firebaseConfig } from './firebaseConfig';

const GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const GSI_SCRIPT_ID = 'jidoka-google-gsi-client';
const GSI_LOAD_TIMEOUT_MS = 15_000;

interface GoogleCredentialResponse {
  credential?: string;
  select_by?: string;
}

interface GoogleIdConfiguration {
  client_id: string;
  callback: (response: GoogleCredentialResponse) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
  context?: 'signin';
  hd?: string;
  ux_mode?: 'popup';
  use_fedcm_for_button?: boolean;
}

interface GoogleButtonConfiguration {
  type: 'standard';
  theme: 'outline';
  size: 'large';
  text: 'continue_with';
  shape: 'pill';
  logo_alignment: 'left';
  width: number;
}

interface GoogleAccountsId {
  initialize: (config: GoogleIdConfiguration) => void;
  renderButton: (parent: HTMLElement, config: GoogleButtonConfiguration) => void;
  prompt: () => void;
  disableAutoSelect: () => void;
}

declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: GoogleAccountsId;
      };
    };
  }
}

export interface GoogleSignInButtonOptions {
  onCredential: (idToken: string) => void | Promise<void>;
  onError: (error: Error) => void;
}

let gsiLoader: Promise<void> | null = null;

const googleIdentityApiReady = (): boolean => Boolean(window.google?.accounts?.id);

const loadGsiClient = (): Promise<void> => {
  if (googleIdentityApiReady()) return Promise.resolve();
  if (gsiLoader) return gsiLoader;

  gsiLoader = new Promise<void>((resolve, reject) => {
    // A prior hot reload or failed request can leave an inert script element.
    // If the API is not present, replace it instead of waiting on an event that
    // may already have fired.
    document.getElementById(GSI_SCRIPT_ID)?.remove();

    const script = document.createElement('script');
    script.id = GSI_SCRIPT_ID;
    script.src = GSI_SCRIPT_SRC;
    script.async = true;
    script.defer = true;

    const timeout = window.setTimeout(() => {
      script.remove();
      gsiLoader = null;
      reject(Object.assign(new Error('Google sign-in took too long to load'), {
        code: 'auth/network-request-failed'
      }));
    }, GSI_LOAD_TIMEOUT_MS);

    script.onload = () => {
      window.clearTimeout(timeout);
      if (googleIdentityApiReady()) {
        resolve();
        return;
      }

      script.remove();
      gsiLoader = null;
      reject(Object.assign(new Error('Google Identity Services unavailable'), {
        code: 'auth/operation-not-supported-in-this-environment'
      }));
    };
    script.onerror = () => {
      window.clearTimeout(timeout);
      script.remove();
      gsiLoader = null;
      reject(Object.assign(new Error('Failed to load Google Identity Services'), {
        code: 'auth/network-request-failed'
      }));
    };
    document.head.appendChild(script);
  });

  return gsiLoader;
};

/**
 * Render Google's official authentication control and return only Google ID
 * tokens. OAuth access-token authorization is intentionally not used for login.
 */
export const renderGoogleSignInButton = async (
  container: HTMLElement,
  options: GoogleSignInButtonOptions
): Promise<void> => {
  if (!firebaseConfig.googleClientId) {
    throw Object.assign(new Error('Google client ID is not configured'), { code: 'auth/invalid-api-key' });
  }

  await loadGsiClient();
  const googleIdentity = window.google?.accounts?.id;
  if (!googleIdentity) {
    throw Object.assign(new Error('Google Identity Services unavailable'), {
      code: 'auth/operation-not-supported-in-this-environment'
    });
  }

  const availableWidth = Math.floor(container.getBoundingClientRect().width || 320);
  const buttonWidth = Math.max(200, Math.min(400, availableWidth));

  container.replaceChildren();
  container.classList.add('google-identity-button-host');

  googleIdentity.initialize({
    client_id: firebaseConfig.googleClientId,
    callback: response => {
      if (!response.credential) {
        options.onError(Object.assign(new Error('Google did not return an identity credential'), {
          code: 'auth/google-sign-in-failed'
        }));
        return;
      }

      void options.onCredential(response.credential);
    },
    auto_select: true,
    cancel_on_tap_outside: true,
    context: 'signin',
    hd: 'voltera.io',
    ux_mode: 'popup',
    use_fedcm_for_button: true
  });

  googleIdentity.renderButton(container, {
    type: 'standard',
    theme: 'outline',
    size: 'large',
    text: 'continue_with',
    shape: 'pill',
    logo_alignment: 'left',
    width: buttonWidth
  });
};

export const isStandaloneDisplayMode = (): boolean => {
  if (typeof window === 'undefined') return false;
  const mediaStandalone = window.matchMedia?.('(display-mode: standalone)').matches === true;
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return mediaStandalone || iosStandalone;
};

const AUTO_SELECT_DISABLED_KEY = 'JIDOKA_GIS_AUTO_SELECT_DISABLED';

/**
 * Record an explicit sign-out. GIS is not loaded on app pages, so the login
 * page applies Google's disableAutoSelect() before the next One Tap prompt;
 * otherwise auto-select would immediately sign the user back in.
 */
export const disableGoogleAutoSelectOnNextPrompt = (): void => {
  try { localStorage.setItem(AUTO_SELECT_DISABLED_KEY, '1'); } catch { /* Auto-select stays enabled. */ }
};

/**
 * Show Google One Tap after the sign-in button is rendered. Returning users
 * with one Google session are signed in automatically; after an explicit
 * sign-out Google asks for a click instead.
 */
export const promptGoogleOneTap = (): void => {
  const googleIdentity = window.google?.accounts?.id;
  if (!googleIdentity) return;
  try {
    if (localStorage.getItem(AUTO_SELECT_DISABLED_KEY) === '1') {
      googleIdentity.disableAutoSelect();
      localStorage.removeItem(AUTO_SELECT_DISABLED_KEY);
    }
  } catch { /* Storage unavailable; Google still honors its own cooldowns. */ }
  googleIdentity.prompt();
};
